<template>
  <div class="home-slider">
    <div class="slider-content">
      <div 
        v-for="(slide, index) in slides" 
        :key="slide.id || index" 
        class="slide"
        :class="{ 'active': index === currentIndex }"
      >
        <div class="parallax-container">
          <div v-if="slide.mediaType === 'image'" 
               class="parallax-bg" 
               :style="{backgroundImage: `url(${slide.mediaUrl})`}">
          </div>
          <div v-else-if="slide.mediaType === 'video'" class="parallax-bg video-container">
            <video autoplay loop muted :src="slide.mediaUrl" class="video-bg"></video>
          </div>
        </div>
        <div class="slide-details">
          <SliderLoader 
            :duration="autoplayDuration" 
            :is-active="autoplay" 
            :reset="loaderReset" 
            @completed="nextSlide" 
          />
          <h3>{{ slide.title }}</h3>
          <p>{{ slide.tag }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import SliderLoader from './SliderLoader.vue';

interface Slide {
  id?: string | number;
  mediaType?: 'image' | 'video';
  mediaUrl?: string;
  title?: string;
  tag?: string;
}

const props = defineProps({
  slides: {
    type: Array as () => Slide[],
    default: () => []
  },
  autoplay: {
    type: Boolean,
    default: true
  },
  autoplayDuration: {
    type: Number,
    default: 15000 // 15 seconds
  }
});

const currentIndex = ref(0);
const loaderReset = ref(false);

// Calculate total number of slides with fallback to empty array
const totalSlides = computed(() => props.slides?.length || 0);

const nextSlide = () => {
  if (totalSlides.value === 0) return;
  
  currentIndex.value = (currentIndex.value + 1) % totalSlides.value;
  resetLoader();
};

const prevSlide = () => {
  if (totalSlides.value === 0) return;
  
  currentIndex.value = (currentIndex.value - 1 + totalSlides.value) % totalSlides.value;
  resetLoader();
};

const goToSlide = (index: number) => {
  currentIndex.value = index;
  resetLoader();
};

const resetLoader = () => {
  loaderReset.value = true;
  setTimeout(() => {
    loaderReset.value = false;
  }, 50);
};

let autoplayInterval: NodeJS.Timeout | null = null;

onMounted(() => {
  if (props.autoplay && totalSlides.value > 1) {
    autoplayInterval = setInterval(nextSlide, props.autoplayDuration);
  }
});

onBeforeUnmount(() => {
  if (autoplayInterval) {
    clearInterval(autoplayInterval);
  }
});
</script>

<style scoped>
.home-slider {
  position: relative;
  width: 100%;
  height: 100%;
  aspect-ratio: 1286 / 768;
  overflow: hidden;
  border-radius: 10px;
  background-color: #000;
}

.slider-content {
  display: flex;
  width: 100%;
  height: 100%;
  transition: none;
}

.slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.slide.active {
  opacity: 1;
  pointer-events: auto;
}

.parallax-container {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.parallax-bg {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center center;
  position: relative;
  transform: translateZ(0);
  will-change: transform;
}

/* CSS-based parallax effect */
@media (min-width: 768px) {
  .parallax-bg {
    background-attachment: fixed;
  }
}

.video-container {
  height: 100%;
  width: 100%;
  overflow: hidden;
  position: relative;
}

.video-bg {
  position: absolute;
  top: 50%;
  left: 50%;
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  transform: translateX(-50%) translateY(-50%);
  object-fit: cover;
}

.slide-details {
  position: absolute;
  bottom: 0;
  left: 0;
  padding: 20px;
  margin-left: 20px;
  color: white;
  z-index: 3;
}

.slide-details h3 {
  margin: 0 0 5px 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.2;
}

.slide-details p {
  margin: 0;
  font-size: 10px;
  font-weight: 400;
}

.slider-controls {
  position: absolute;
  bottom: 20px;
  right: 40px;
  display: flex;
  justify-content: flex-end;
  padding: 0;
  z-index: 4;
}

.slider-dots {
  display: flex;
  gap: 10px;
}

.slider-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.5);
  border: none;
  padding: 0;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.2s;
}

.slider-dot:hover {
  background-color: rgba(255, 255, 255, 0.8);
}

.slider-dot.active {
  background-color: white;
  transform: scale(1.2);
}
@media (max-width: 480px) {
  .home-slider {
    aspect-ratio: 358/456;
    height: auto;
  }
  .slide-details {
    margin-left: 16px;
    padding-left: 0;
    padding-top: 16px;
  }
}
</style> 